/**
 * useContactDealsForChat - Hook para buscar deals de um contato na sidebar do chat
 * Usa RPC segura get_contact_deals_for_chat com filtro de segurança por vendedor
 * 
 * Versão: 1.0.0
 * - Admin/Supervisor/Owner vê todos os deals do contato
 * - Vendedor vê apenas seus próprios deals
 * - Avatar do vendedor usa COALESCE igual ao padrão do get_deal_by_id
 * 
 * RELEASE: 2026-01-14
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para o módulo
const logger = createLogger('useContactDealsForChat');

/**
 * Interface para um deal retornado na sidebar do chat
 * Inclui dados básicos + informações de pipeline/fase + avatar do vendedor
 */
export interface ContactDealForChat {
  id: string;
  title: string;
  value: number | null;
  currency: string | null;
  status: 'open' | 'won' | 'lost';
  priority: string | null;
  probability: number | null;
  expected_close_date: string | null;
  created_at: string;
  pipeline_id: string | null;
  pipeline_name: string | null;
  phase_id: string | null;
  phase_name: string | null;
  phase_color: string | null;
  seller_id: string | null;
  seller_name: string | null;
  seller_avatar: string | null; // COALESCE já aplicado na RPC
}

/**
 * Opções para o hook com suporte a lazy loading
 */
export interface UseContactDealsForChatOptions {
  /** Ativa/desativa a query (para lazy loading) */
  enabled?: boolean;
}

/**
 * Hook para buscar deals de um contato na sidebar do chat
 * Implementa segurança em nível de banco via RPC
 * 
 * @param contactId - ID do contato para buscar deals
 * @param options - Opções de configuração (lazy loading)
 * @returns Query result com lista de deals e estados de loading/error
 */
export function useContactDealsForChat(
  contactId: string | null,
  options?: UseContactDealsForChatOptions
) {
  const { enabled = true } = options || {};
  
  return useQuery({
    queryKey: ['contact-deals-for-chat', contactId],
    queryFn: async (): Promise<ContactDealForChat[]> => {
      if (!contactId) {
        return [];
      }
      
      logger.debug('Buscando deals do contato para chat', { contactId });
      
      // Chama RPC segura com filtro de vendedor
      const { data, error } = await supabase.rpc('get_contact_deals_for_chat', {
        p_contact_id: contactId,
        p_tenant_hint: null // Tenant é validado internamente na RPC
      });
      
      if (error) {
        logger.error('Erro ao buscar deals do contato', { error, contactId });
        throw error;
      }
      
      logger.debug('Deals do contato carregados', { 
        contactId, 
        count: data?.length || 0 
      });
      
      // Retorna com tipagem correta
      return (data || []) as ContactDealForChat[];
    },
    enabled: enabled && !!contactId,
    staleTime: 30000, // 30 segundos de cache
    refetchOnWindowFocus: false,
  });
}
